/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import { lazy, FC, useState, useEffect } from 'react'
import { Routes, Route, BrowserRouter, Navigate } from 'react-router-dom'
import { PrivateRoutes } from './PrivateRoutes'
// import {ErrorsPage} from '../modules/errors/ErrorsPage'
import { AuthPage } from '../modules/auth'
import { App } from '../App'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '../redux/store'

// page import 
const ResetPasswordPage = lazy(() => import('../pages/ResetPasswordPage'))
const ESignAuthPage = lazy(() => import('../pages/ESignAuthPage'))
const TenancyRenewalPage = lazy(() => import('../pages/TenancyRenewalPage'))
const InvoicePreviewPage = lazy(() => import('../pages/InvoicePreviewPage'))
const ReceiptPreviewPage = lazy(() => import('../pages/ReceiptPreviewPage'))
const DebitNotePreviewPage = lazy(() => import('../pages/DebitNotePreviewPage'))
const CreditNotePreviewPage = lazy(() => import('../pages/CreditNotePreviewPage'))
const ReminderPreviewPage = lazy(() => import('../pages/ReminderPreviewPage'))
const PaymentStatusPage = lazy(() => import('../pages/PaymentStatusPage'))
const TenantSignUpPage = lazy(() => import('../pages/TenantSignUpPage'))
const CustomerSignUpPage = lazy(() => import('../pages/CustomerSignUpPage'))
const PaymentVoucherPreviewPage = lazy(() => import('../pages/PaymentVoucherPreviewPage'))
const ProductPaymentPreviewPage = lazy(() => import('../pages/owner-subscriptions/ProductPaymentPreviewPage'))
const PaymentSummaryPage = lazy(() => import('../pages/owner-subscriptions/PaymentSummaryPage'))
const StoreCheckoutPage = lazy(() => import('../pages/StoreCheckoutPage'))

/**
 * Base URL of the website.
 *
 * @see https://facebook.github.io/create-react-app/docs/using-the-public-folder
 */
// const {PUBLIC_URL} = process.env

const AppRoutes: FC = () => {
  const userSlice = useSelector((state: RootState) => state.user)

  return (
    <BrowserRouter basename={'/'}>
      <Routes>

        <Route element={<App />}>
          <Route path='/reset-password/:secretKey' element={<ResetPasswordPage />} />
          <Route path='/e-tenancies/signature/auth/:secretKey' element={<ESignAuthPage />} />
          <Route path='/agreement/signature/auth/:secretKey' element={<ESignAuthPage />} />
          <Route path='/tenancy-renewal/:secretKey' element={<TenancyRenewalPage />} />
          <Route path='/accounting/bill/payment/:status/:secretKey' element={<PaymentStatusPage />} />
          <Route path='/service-cart/store/payment/:status/:secretKey' element={<PaymentStatusPage />} />
          <Route path='/signuplink/:secretKey' element={<TenantSignUpPage />} />
          <Route path='/accounting/invoice/preview/:id' element={<InvoicePreviewPage />} />
          <Route path='/accounting/payment-voucher/preview/:id' element={<PaymentVoucherPreviewPage />} />
          <Route path='/accounting/credit-note/preview/:id' element={<CreditNotePreviewPage />} />
          <Route path='/accounting/debit-note/preview/:id' element={<DebitNotePreviewPage />} />
          <Route path='/service-cart/invoice/preview/:id' element={<InvoicePreviewPage />} />
          <Route path='/service-cart/receipt/preview/:id' element={<ReceiptPreviewPage />} />
          <Route path='/accounting/owner-bill/preview/:id' element={<ProductPaymentPreviewPage />} />
          <Route path='/accounting/owner-bill/payment/:id' element={<PaymentSummaryPage />} />
          <Route path='/customer/signuplink/:secretKey' element={<CustomerSignUpPage />} />
          <Route path='/customer/completelink/:secretKey' element={<CustomerSignUpPage />} />
          {userSlice.role === 'Tenant' &&
            <Route path='/reminder-payment-letter/preview/:secretKey' element={<ReminderPreviewPage />} />
          }
          {userSlice.role !== 'Landlord' &&
            <>
              <Route path='/accounting/receipt/preview/:id' element={<ReceiptPreviewPage />} />
            </>
          }
          {userSlice.role === 'Owner' && // access module was archived
            <Route path='/owner/payment/:transactionId' element={<StoreCheckoutPage />} />
          }
          {userSlice?._id ? (
            <>
              <Route path='/*' element={<PrivateRoutes />} />
            </>
          ) : (
            <>
              <Route path='/' element={<AuthPage />} />
              <Route path='*' element={<Navigate to='/' />} />
            </>
          )}
        </Route>
      </Routes>
    </BrowserRouter>
  )
}

export { AppRoutes }
